/**
 * PublicProfilePage - Pagina pubblica del profilo aziendale
 * Accessibile senza autenticazione a /{slug}
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Globe,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  ArrowLeft,
  Building2,
  Tag,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface PublicProfile {
  id: string;
  name: string;
  description?: string;
  city?: string;
  province?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  type?: string;
  categories?: string[];
}

const TYPE_LABELS: Record<string, string> = {
  producer: 'Produttore',
  distributor: 'Distributore',
  agent: 'Agente',
  buyer: 'Acquirente',
  other: 'Azienda',
};

export default function PublicProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/associations/public/profile/${slug}`);
        setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex flex-col items-center justify-center text-white px-4">
        <Building2 className="w-16 h-16 text-gray-600 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Profilo non trovato</h1>
        <p className="text-gray-400 mb-6 text-center">
          Questo profilo non esiste o non è più disponibile.
        </p>
        <Link to="/auth">
          <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla home
          </Button>
        </Link>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[profile.type || 'other'] || 'Azienda';

  return (
    <div className="min-h-screen bg-[#0f1117] text-white">
      {/* Header bar */}
      <div className="border-b border-white/10 bg-[#0f1117]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/auth" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" />
            OPS!
          </Link>
          <Link to="/auth">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs">
              Accedi alla piattaforma
            </Button>
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero section */}
        <div className="flex flex-col sm:flex-row items-start gap-8 mb-12">
          {/* Logo */}
          <div className="flex-shrink-0">
            {profile.logo_url ? (
              <img
                src={profile.logo_url}
                alt={profile.name}
                className="w-24 h-24 rounded-2xl object-contain bg-white/5 border border-white/10 p-2"
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-green-900/40 to-green-700/20 border border-green-800/30 flex items-center justify-center">
                <Building2 className="w-10 h-10 text-green-500/60" />
              </div>
            )}
          </div>

          {/* Info principale */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold tracking-tight">{profile.name}</h1>
              <Badge
                variant="outline"
                className="border-green-700/50 text-green-400 text-xs font-medium"
              >
                {typeLabel}
              </Badge>
            </div>

            {(profile.city || profile.province) && (
              <div className="flex items-center gap-1.5 text-gray-400 text-sm mb-3">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {[profile.city, profile.province].filter(Boolean).join(', ')}
                </span>
              </div>
            )}

            {profile.description && (
              <p className="text-gray-300 leading-relaxed max-w-2xl">
                {profile.description}
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/8 mb-8" />

        {/* Contatti + Categorie */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contatti */}
          <div>
            <h2 className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">Contatti</h2>
            <div className="space-y-3">
              {profile.phone && (
                <a
                  href={`tel:${profile.phone}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
                  data-testid="public-profile-phone"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-green-700/50 transition-colors">
                    <Phone className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-400 transition-colors" />
                  </div>
                  <span className="text-sm">{profile.phone}</span>
                </a>
              )}
              {profile.email && (
                <a
                  href={`mailto:${profile.email}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
                  data-testid="public-profile-email"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-green-700/50 transition-colors">
                    <Mail className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-400 transition-colors" />
                  </div>
                  <span className="text-sm">{profile.email}</span>
                </a>
              )}
              {profile.website && (
                <a
                  href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
                  data-testid="public-profile-website"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-green-700/50 transition-colors">
                    <Globe className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-400 transition-colors" />
                  </div>
                  <span className="text-sm flex items-center gap-1">
                    {profile.website.replace(/^https?:\/\//, '')}
                    <ExternalLink className="w-3 h-3" />
                  </span>
                </a>
              )}
              {!profile.phone && !profile.email && !profile.website && (
                <p className="text-gray-600 text-sm">Nessun contatto disponibile</p>
              )}
            </div>
          </div>

          {/* Categorie */}
          {profile.categories && profile.categories.length > 0 && (
            <div>
              <h2 className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" />
                Settori
              </h2>
              <div className="flex flex-wrap gap-2" data-testid="public-profile-categories">
                {profile.categories.map((cat) => (
                  <Badge
                    key={cat}
                    variant="secondary"
                    className="bg-white/5 border border-white/10 text-gray-300 text-xs font-normal"
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 pt-8 border-t border-white/8">
          <div className="bg-gradient-to-br from-green-950/40 to-transparent border border-green-900/30 rounded-2xl p-6 text-center">
            <p className="text-gray-400 text-sm mb-4">
              Sei un operatore HORECA? Accedi a OPS! per richiedere un preventivo o avviare una collaborazione.
            </p>
            <Link to="/auth">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="public-profile-cta"
              >
                Accedi a OPS!
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/8 mt-12 py-6 text-center text-xs text-gray-600">
        <span>Powered by </span>
        <Link to="/auth" className="hover:text-gray-400 transition-colors">OPS!</Link>
      </div>
    </div>
  );
}
